import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services';
import BreadCrumb from '../components/Common/BreadCrumb';
import { toast } from 'react-toastify';

const ProfilePage: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState<{
    name: string;
    email?: string;
    phone?: string;
    role?: string;
    avatar?: string;
  } | null>(null);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await authService.getProfile();
      setProfile(data);
      setForm({
        name: data?.name || '',
        phone: data?.phone || '',
      });
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    setForm({
      name: profile?.name || '',
      phone: profile?.phone || '',
    });
    setAvatarFile(null);
    setAvatarPreview(null);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file (jpg, png, gif, webp)');
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name.trim());
      if (form.phone.trim()) formData.append('phone', form.phone.trim());
      if (avatarFile) formData.append('avatar', avatarFile);
      await authService.updateProfile(formData);
      await refreshProfile();
      await loadProfile();
      setEditing(false);
      setAvatarFile(null);
      setAvatarPreview(null);
      toast.success('Profile updated successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const avatarUrl = avatarPreview || profile?.avatar || null;
  const initials = (profile?.name || user?.name || 'A').charAt(0).toUpperCase();

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="My Profile" />
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading profile...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb pageTitle="My Profile" />
        <Row>
          <Col xs={12}>
            <Card className="shadow-sm border-0">
              <CardBody className="p-4 p-lg-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">
                    <i className="ri-user-settings-line me-2 text-primary"></i>
                    Profile Information
                  </h5>
                  {!editing && (
                    <Button color="primary" outline size="sm" onClick={handleStartEdit}>
                      <i className="ri-edit-line me-1"></i> Edit Profile
                    </Button>
                  )}
                </div>

                {editing ? (
                  <Form onSubmit={handleSubmit}>
                    <div className="d-flex align-items-start gap-4 mb-4">
                      <div className="position-relative">
                        <div
                          className="rounded-circle overflow-hidden d-flex align-items-center justify-content-center"
                          style={{
                            width: 100,
                            height: 100,
                            background: avatarUrl
                              ? undefined
                              : 'linear-gradient(135deg, #405189 0%, #5ea3cb 100%)',
                            backgroundColor: avatarUrl ? '#f0f0f0' : undefined,
                          }}
                        >
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt="Avatar"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <span className="text-white fs-2 fw-bold">{initials}</span>
                          )}
                        </div>
                        <label
                          className="position-absolute bottom-0 end-0 mb-0 me-0 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center cursor-pointer"
                          style={{ width: 32, height: 32 }}
                          htmlFor="avatar-upload"
                        >
                          <i className="ri-camera-line"></i>
                        </label>
                        <Input
                          id="avatar-upload"
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="visually-hidden"
                          onChange={handleAvatarChange}
                        />
                      </div>
                      <div className="flex-grow-1">
                        <FormGroup>
                          <Label>Name</Label>
                          <Input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="Your name"
                            required
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Phone (optional)</Label>
                          <Input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                            placeholder="Phone number"
                          />
                        </FormGroup>
                      </div>
                    </div>
                    <p className="text-muted small mb-3">
                      <i className="ri-information-line me-1"></i>
                      Email cannot be changed. Avatar: jpg, png, gif or webp.
                    </p>
                    <div className="d-flex gap-2">
                      <Button color="primary" type="submit" disabled={saving}>
                        {saving ? <Spinner size="sm" className="me-2" /> : null}
                        <i className="ri-save-line me-1"></i> Save Changes
                      </Button>
                      <Button color="secondary" outline type="button" onClick={handleCancelEdit} disabled={saving}>
                        Cancel
                      </Button>
                    </div>
                  </Form>
                ) : (
                  <div className="profile-view">
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="rounded-circle overflow-hidden me-4 d-flex align-items-center justify-content-center"
                        style={{
                          width: 100,
                          height: 100,
                          background: profile?.avatar
                            ? undefined
                            : 'linear-gradient(135deg, #405189 0%, #5ea3cb 100%)',
                          backgroundColor: profile?.avatar ? '#f0f0f0' : undefined,
                        }}
                      >
                        {profile?.avatar ? (
                          <img
                            src={profile.avatar}
                            alt="Avatar"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <span className="text-white fs-2 fw-bold">{initials}</span>
                        )}
                      </div>
                      <div>
                        <h5 className="mb-1">{profile?.name || user?.name || 'Admin'}</h5>
                        <p className="text-muted mb-0">{profile?.role || user?.role || 'Administrator'}</p>
                        {profile?.email && (
                          <p className="text-muted small mb-0 mt-1">{profile.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="border-top pt-4 mt-4">
                      <div className="row g-4">
                        <div className="col-sm-6 col-lg-3">
                          <label className="text-muted small text-uppercase d-block mb-1">Name</label>
                          <p className="mb-0 fw-medium">{profile?.name || '-'}</p>
                        </div>
                        <div className="col-sm-6 col-lg-3">
                          <label className="text-muted small text-uppercase d-block mb-1">Email</label>
                          <p className="mb-0 fw-medium">{profile?.email || user?.email || '-'}</p>
                        </div>
                        <div className="col-sm-6 col-lg-3">
                          <label className="text-muted small text-uppercase d-block mb-1">Phone</label>
                          <p className="mb-0 fw-medium">{profile?.phone || '-'}</p>
                        </div>
                        <div className="col-sm-6 col-lg-3">
                          <label className="text-muted small text-uppercase d-block mb-1">Role</label>
                          <p className="mb-0 fw-medium">{profile?.role || user?.role || '-'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ProfilePage;
